import React, { useState } from 'react'
import { ChevronRight, Code, Database, ExternalLink, MessageSquare, Network, Server, Settings, Terminal, Trash2, Zap } from 'lucide-react'
import type { Host, Service, ToolFunction, SystemdUnit, ListeningPort, ProcessInfo } from '../types'

interface HostTreeProps {
  hosts: Record<string, Host>
  external: Record<string, Service>
  selectedHost: string
  onHostSelect: (host: string) => void
  onServiceSelect: (service: Service) => void
  onDeleteForgeJob?: (service: Service) => void
  deletingId?: string | null
}

const getStatusColor = (status: string) => {
  if (status === 'running' || status === 'connected' || status === 'deployed') return 'text-[#2ee6c0]'
  if (status === 'lan-unprobed' || status === 'unknown') return 'text-zinc-500'
  if (status.includes('timeout') || status.includes('refused') || status === 'error' || status === 'failed' || status === 'down' || status === 'origin-down') return 'text-[#f43f5e]'
  return 'text-[#f59e0b]'
}

const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  read: Database,
  write: Zap,
  exec: Terminal,
  admin: Settings,
  chat: MessageSquare,
  mesh: Network,
}

const CATEGORY_COLORS: Record<string, string> = {
  read: 'text-blue-400 border-blue-400/30',
  write: 'text-green-400 border-green-400/30',
  exec: 'text-orange-400 border-orange-400/30',
  admin: 'text-purple-400 border-purple-400/30',
  chat: 'text-pink-400 border-pink-400/30',
  mesh: 'text-cyan-400 border-cyan-400/30',
}

function StatusBadge({ status }: { status: string }) {
  const isUp = status === 'running' || status === 'connected' || status === 'deployed' || status === 'up' || status === 'healthy' || status === 'OK'
  const isGated = status === 'gated' || status === 'access-gated' || status === 'AUTH-GATED' || status === '401'
  const isDown = status === 'down' || status === 'error' || status === 'DOWN' || status === 'failed' || status.includes('timeout') || status.includes('refused')
  
  if (isUp) return <span className="px-2 py-0.5 text-[10px] font-mono bg-green-500/20 text-green-400 border border-green-400/30 rounded">UP</span>
  if (isGated) return <span className="px-2 py-0.5 text-[10px] font-mono bg-blue-500/20 text-blue-400 border border-blue-400/30 rounded">GATED</span>
  if (isDown) return <span className="px-2 py-0.5 text-[10px] font-mono bg-red-500/20 text-red-400 border border-red-400/30 rounded">DOWN</span>
  return <span className="px-2 py-0.5 text-[10px] font-mono bg-yellow-500/20 text-yellow-400 border border-yellow-400/30 rounded">{status.toUpperCase()}</span>
}

function lanHref(svc: Service): string | null {
  const meta = svc.metadata || {}
  if (typeof meta.lan_url === 'string' && meta.lan_url.startsWith('http://')) return meta.lan_url
  if (svc.url.startsWith('http://') && /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(svc.host)) {
    return svc.url
  }
  if (/^10\.\d+\.\d+\.\d+$/.test(svc.host) && svc.port) {
    return `http://${svc.host}:${svc.port}`
  }
  return null
}

function ServiceToolsInline({ service }: { service: Service }) {
  if (!service.tools || service.tools.length === 0) return null
  
  const tools = service.tools.slice(0, 4)
  const more = service.tools.length - 4
  
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {tools.map(tool => {
        const Icon = CATEGORY_ICONS[tool.category] || Settings
        const colorClass = CATEGORY_COLORS[tool.category] || 'text-zinc-400'
        return (
          <button
            key={`${tool.method}-${tool.path}`}
            className={`flex items-center gap-1 px-2 py-0.5 text-[9px] font-mono rounded border transition-colors ${colorClass} border-current/30 bg-current/10 hover:bg-current/20`}
            title={`${tool.method} ${tool.path} - ${tool.description}`}
          >
            <Icon className="w-2.5 h-2.5" />
            <span>{tool.method}</span>
            <code className="px-1 bg-white/5 rounded">{tool.path}</code>
          </button>
        )
      })}
      {more > 0 && (
        <span className="px-2 py-0.5 text-[9px] font-mono text-zinc-500 border border-zinc-500/30 rounded">
          +{more} more
        </span>
      )}
    </div>
  )
}

function SystemdUnitRow({ unit }: { unit: SystemdUnit }) {
  const isActive = unit.active === 'active' && unit.sub === 'running'
  const isFailed = unit.active === 'failed' || unit.sub === 'failed'
  
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 hover:bg-white/5 rounded text-xs">
      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-green-400' : isFailed ? 'bg-red-400' : 'bg-yellow-400'}`} />
      <span className="font-mono text-cyan-300 w-40 truncate">{unit.name}</span>
      <span className="text-zinc-500 w-16">{unit.active}</span>
      <span className="text-zinc-500 w-16">{unit.sub}</span>
      <span className="text-zinc-400 truncate flex-1">{unit.description}</span>
    </div>
  )
}

function PortRow({ port }: { port: ListeningPort }) {
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 hover:bg-white/5 rounded text-xs">
      <span className="font-mono text-cyan-300 w-10">{port.protocol.toUpperCase()}</span>
      <span className="text-zinc-300 w-24 font-mono">{port.local_address}:{port.local_port}</span>
      <span className="text-zinc-500 w-16">{port.state}</span>
      <span className="text-zinc-400 truncate flex-1">{port.process_name || '-'}</span>
      {port.pid && <span className="text-zinc-500 font-mono">PID:{port.pid}</span>}
    </div>
  )
}

function ProcessRow({ proc }: { proc: ProcessInfo }) {
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 hover:bg-white/5 rounded text-xs">
      <span className="font-mono text-cyan-300 w-10">{proc.pid}</span>
      <span className="text-zinc-500 w-16">{proc.user}</span>
      <span className="text-zinc-400 w-12">{proc.cpu_percent.toFixed(1)}%</span>
      <span className="text-zinc-400 w-12">{proc.mem_percent.toFixed(1)}%</span>
      <span className="text-zinc-300 truncate flex-1 font-mono">{proc.command}</span>
    </div>
  )
}

export function HostTree({ hosts, external, selectedHost, onHostSelect, onServiceSelect, onDeleteForgeJob, deletingId }: HostTreeProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    Object.keys(hosts).forEach((k) => { initial[k] = true })
    return initial
  })
  const [viewMode, setViewMode] = useState<'services' | 'systemd' | 'ports' | 'processes'>('services')

  return (
    <div className="space-y-4">
      {/* View Mode Tabs */}
      <div className="flex items-center gap-2 border-b border-white/5 pb-2">
        <span className="text-[10px] tracking-[0.2em] text-zinc-500 flex items-center gap-2">
          <Server className="w-3 h-3" /> MSI-DEBIAN TOOLS & FUNCTIONS
        </span>
        <div className="ml-auto flex items-center gap-1 bg-white/5 rounded border border-white/10 p-0.5">
          {(['services', 'systemd', 'ports', 'processes'] as const).map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-2.5 py-1 text-[9px] font-mono rounded transition-colors ${
                viewMode === mode
                  ? 'bg-cyan-500/30 text-cyan-300'
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              {mode.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {Object.entries(hosts).map(([hostKey, host]) => {
        const selected = selectedHost === hostKey
        const open = expanded[hostKey] !== false
        return (
          <div key={hostKey} className={`glass-panel rounded-md overflow-hidden ${selected ? 'border-cyan-400/40' : ''}`}>
            <button
              type="button"
              onClick={() => {
                onHostSelect(hostKey)
                setExpanded((prev) => ({ ...prev, [hostKey]: !open }))
              }}
              className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-white/5"
            >
              <Server className="w-4 h-4 text-cyan-400" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm">{host.name}</span>
                  <span className="text-[10px] text-zinc-500">{host.ip}</span>
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">
                  {host.services.length} services · {host.systemd_units?.length || 0} units · {host.listening_ports?.length || 0} ports · LAN-unprobed from edge
                </div>
              </div>
              <ChevronRight className={`w-4 h-4 text-zinc-600 transition-transform ${open ? 'rotate-90' : ''}`} />
            </button>
            {open && (
              <div className="border-t border-white/5 px-2 py-2 space-y-1 max-h-[70vh] overflow-y-auto">
                {viewMode === 'services' && (
                  <>
                    {host.services.map((svc) => {
                      const lan = lanHref(svc)
                      return (
                      <div
                        key={`${svc.name}-${svc.port}`}
                        className="w-full px-3 py-2 flex items-start gap-3 hover:bg-white/5 rounded group"
                      >
                        <button
                          type="button"
                          onClick={() => onServiceSelect(svc)}
                          className="flex-1 min-w-0 text-left flex items-start gap-3"
                        >
                          <span className={getStatusColor(svc.status)}>●</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <div className="text-xs font-medium">{svc.name}</div>
                              <span className="text-[10px] text-zinc-500">:{svc.port}</span>
                            </div>
                            <div className="text-[10px] text-zinc-500">{svc.status}</div>
                            <ServiceToolsInline service={svc} />
                          </div>
                        </button>
                        {lan && (
                          <a
                            href={lan}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="shrink-0 mt-0.5 px-2 py-1 text-[9px] font-mono text-cyan-300 border border-cyan-400/30 rounded hover:bg-cyan-500/10 flex items-center gap-1"
                            title={lan}
                          >
                            <ExternalLink className="w-2.5 h-2.5" />
                            OPEN ON LAN
                          </a>
                        )}
                        {/^forge-job-\d+$/.test(svc.name) && onDeleteForgeJob && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              onDeleteForgeJob(svc)
                            }}
                            disabled={deletingId === svc.name}
                            className="shrink-0 mt-0.5 px-2 py-1 text-[9px] font-mono text-rose-300 border border-rose-400/40 rounded hover:bg-rose-500/15 flex items-center gap-1 disabled:opacity-40"
                            title="Stop unit, delete unit file, delete job files, drop tunnel/DNS"
                          >
                            <Trash2 className="w-2.5 h-2.5" />
                            {deletingId === svc.name ? 'DELETING' : 'DELETE'}
                          </button>
                        )}
                      </div>
                      )
                    })}
                    {host.services.length === 0 && (
                      <div className="px-3 py-4 text-center text-zinc-500 text-sm">No services discovered</div>
                    )}
                  </>
                )}
                
                {viewMode === 'systemd' && (
                  <>
                    <div className="px-2 py-1 text-[10px] tracking-[0.1em] text-zinc-500 uppercase border-b border-white/5 mb-1 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                      <span className="font-mono w-40">UNIT NAME</span>
                      <span className="w-16">LOAD</span>
                      <span className="w-16">ACTIVE</span>
                      <span className="w-16">SUB</span>
                      <span>DESCRIPTION</span>
                    </div>
                    {host.systemd_units && host.systemd_units.length > 0 ? (
                      <>
                        <div className="px-2 py-1 text-[10px] text-zinc-500">{host.systemd_units.length} units (inactive omitted)</div>
                        {host.systemd_units.map(unit => (
                          <SystemdUnitRow key={unit.name} unit={unit} />
                        ))}
                      </>
                    ) : (
                      <div className="px-3 py-4 text-center text-zinc-500 text-sm">No systemd units loaded</div>
                    )}
                  </>
                )}
                
                {viewMode === 'ports' && (
                  <>
                    <div className="px-2 py-1 text-[10px] tracking-[0.1em] text-zinc-500 uppercase border-b border-white/5 mb-1 flex items-center gap-2">
                      <span className="w-10 font-mono">PROTO</span>
                      <span className="w-24 font-mono">ADDRESS:PORT</span>
                      <span className="w-16">STATE</span>
                      <span className="w-24">PROCESS</span>
                      <span>PID</span>
                    </div>
                    {host.listening_ports && host.listening_ports.length > 0 ? (
                      <>
                        <div className="px-2 py-1 text-[10px] text-zinc-500">{host.listening_ports.length} TCP listeners</div>
                        {host.listening_ports.map(port => (
                          <PortRow key={`${port.protocol}-${port.local_address}-${port.local_port}-${port.pid || 0}`} port={port} />
                        ))}
                      </>
                    ) : (
                      <div className="px-3 py-4 text-center text-zinc-500 text-sm">No listening ports discovered</div>
                    )}
                  </>
                )}
                
                {viewMode === 'processes' && (
                  <>
                    <div className="px-2 py-1 text-[10px] tracking-[0.1em] text-zinc-500 uppercase border-b border-white/5 mb-1 flex items-center gap-2">
                      <span className="w-10 font-mono">PID</span>
                      <span className="w-16">USER</span>
                      <span className="w-12">CPU%</span>
                      <span className="w-12">MEM%</span>
                      <span>COMMAND</span>
                    </div>
                    {host.processes && host.processes.length > 0 ? (
                      <>
                        <div className="px-2 py-1 text-[10px] text-zinc-500">Top {host.processes.length} by CPU</div>
                        {host.processes.map(proc => (
                          <ProcessRow key={proc.pid} proc={proc} />
                        ))}
                      </>
                    ) : (
                      <div className="px-3 py-4 text-center text-zinc-500 text-sm">No processes loaded</div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )
      })}

      <div className="text-[10px] tracking-[0.2em] text-zinc-500 flex items-center gap-2 pt-2">
        <Code className="w-3 h-3" /> EXTERNAL SERVICES
      </div>
      <div className="glass-panel rounded-md p-2 space-y-1">
        {Object.entries(external).map(([name, svc]) => (
          <button
            type="button"
            key={name}
            onClick={() => onServiceSelect(svc)}
            className="w-full px-3 py-2 flex items-start gap-3 hover:bg-white/5 rounded text-left"
          >
            <span className={getStatusColor(svc.status)}>●</span>
            <Database className="w-3 h-3 text-zinc-500" />
            <div className="flex-1 min-w-0">
              <div className="text-xs">{name}</div>
              <div className="text-[10px] text-zinc-500 truncate">{svc.url}</div>
              <ServiceToolsInline service={svc} />
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}